import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PreKPracticeSettings } from '../types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';
import { NUMERAL_FONT_STYLE } from '../../PreKAssessment/preKConstants';
import {
  PracticeHeader,
  PRACTICE_PHASES,
  PRACTICE_TIMER_CONSTANTS,
  generatePracticeAnswerChoices,
  PREK_PRACTICE_TRACKS,
} from '../shared';

interface NumberSequence {
  id: string;
  targetNumber: number;
  sequence: number[];
}

interface WhatComesNextWeDoScreenProps {
  settings: PreKPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];
  onComplete: () => void;
  onBack: () => void;
}

const WhatComesNextWeDoScreen: React.FC<WhatComesNextWeDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedQueue, setFailedQueue] = useState<{ factId: string; digit: number }[]>([]);
  const [isRetryPhase, setIsRetryPhase] = useState(false);
  const [currentRetryIndex, setCurrentRetryIndex] = useState(0);

  const [currentSequence, setCurrentSequence] = useState<NumberSequence | null>(null);
  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [highlightCorrect, setHighlightCorrect] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());
  const [showSequence, setShowSequence] = useState(false);

  const { playTTSWithCallback, playChime, isTTSPlaying } = usePreKTTS();

  // Generate number sequence from target (target 3 = [1,2], target 4 = [2,3], target 5 = [3,4])
  const generateSequenceFromTarget = useCallback((targetNumber: number): NumberSequence => {
    const sequence = [targetNumber - 2, targetNumber - 1];

    return {
      id: `sequence-${Date.now()}-${Math.random()}`,
      targetNumber,
      sequence
    };
  }, []);

  // Get current fact
  const getCurrentFact = useCallback(() => {
    if (isRetryPhase) {
      return failedQueue[currentRetryIndex];
    }
    return factsToPractice[currentFactIndex];
  }, [isRetryPhase, failedQueue, currentRetryIndex, factsToPractice, currentFactIndex]);

  const currentFact = getCurrentFact();
  const currentTarget = currentFact?.digit;

  // Initialize sequence and answer choices when target changes
  useEffect(() => {
    if (currentTarget !== undefined) {
      const newSequence = generateSequenceFromTarget(currentTarget);
      setCurrentSequence(newSequence);
      setAnswerChoices(generatePracticeAnswerChoices(PREK_PRACTICE_TRACKS.WHATCOMESNEXT, currentTarget));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setHighlightCorrect(false);
      setCorrectAnswerGiven(false);
      setShowSequence(false);

      // Small delay then show sequence
      setTimeout(() => {
        setShowSequence(true);
      }, 500);
    }
  }, [currentTarget, generateSequenceFromTarget]);

  // Play TTS instruction when component loads
  useEffect(() => {
    if (!ttsHasPlayed && settings.speechEnabled && showSequence) {
      playTTSWithCallback(QUESTION_SCREEN_AUDIO.whatComesNextWedo[currentTarget as 3 | 4 | 5 | 6 | 7 | 8 | 9], () => {
        setTtsHasPlayed(true);
        // Show correct answer highlight briefly after TTS
        setTimeout(() => {
          setHighlightCorrect(true);
          setTimeout(() => setHighlightCorrect(false), PRACTICE_TIMER_CONSTANTS.WE_DO_HIGHLIGHT_MS);
        }, 500);
      }).catch(() => {
        setTtsHasPlayed(true);
        // Still show highlight even if TTS fails
        setTimeout(() => {
          setHighlightCorrect(true);
          setTimeout(() => setHighlightCorrect(false), PRACTICE_TIMER_CONSTANTS.WE_DO_HIGHLIGHT_MS);
        }, 500);
      });
    } else if (!settings.speechEnabled && showSequence) {
      // If speech disabled, still show the highlight
      setTtsHasPlayed(true);
      setTimeout(() => {
        setHighlightCorrect(true);
        setTimeout(() => setHighlightCorrect(false), PRACTICE_TIMER_CONSTANTS.WE_DO_HIGHLIGHT_MS);
      }, 1000);
    }
  }, [showSequence, settings.speechEnabled, playTTSWithCallback, ttsHasPlayed]);

  const advanceToNext = useCallback(() => {
    if (isRetryPhase) {
      // In retry phase - after removing an item, check if queue is empty
      if (failedQueue.length === 0) {
        // Queue is empty, we're done!
        onComplete();
      } else if (currentRetryIndex >= failedQueue.length) {
        // Current index is beyond queue length, reset to 0
        setCurrentRetryIndex(0);
      } else if (currentRetryIndex < failedQueue.length - 1) {
        // Move to next item in retry queue
        setCurrentRetryIndex(currentRetryIndex + 1);
      } else {
        // At end of retry queue, restart from beginning
        setCurrentRetryIndex(0);
      }
    } else {
      // In original phase
      if (currentFactIndex < factsToPractice.length - 1) {
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        // Finished original sequence
        if (failedQueue.length > 0) {
          // Start retry phase
          setIsRetryPhase(true);
          setCurrentRetryIndex(0);
        } else {
          // All done!
          onComplete();
        }
      }
    }

    // Reset answer state for new question
    setHasAnswered(false);
    setCorrectAnswerGiven(false);
    setTtsHasPlayed(false);
    setShowSequence(false);
  }, [isRetryPhase, currentRetryIndex, failedQueue.length, currentFactIndex, factsToPractice.length, onComplete]);

  const handleAnswerClick = useCallback((selectedNumber: number) => {
    // Don't allow clicks while TTS is playing or already answered
    if (isTTSPlaying || hasAnswered) return;

    setHasAnswered(true);
    const isCorrect = selectedNumber === currentTarget;

    if (isCorrect) {
      setCorrectAnswerGiven(true);

      // Update fact score: +1 for correct
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore + 1;
        newScores.set(currentFact.factId, newScore);

        // Only increment counter if going from 0 to 1 (first genuine correct)
        if (currentScore === 0 && newScore === 1) {
          setCompletedCount(prevCount => prevCount + 1);
        }

        return newScores;
      });

      if (isRetryPhase) {
        // In retry phase - remove from failed queue
        setFailedQueue(prev => {
          const newQueue = prev.filter((_, index) => index !== currentRetryIndex);
          // Check if we need to complete after this removal
          if (newQueue.length === 0) {
            setTimeout(() => onComplete(), 600);
          } else {
            // Adjust currentRetryIndex after removal
            setTimeout(() => {
              if (currentRetryIndex >= newQueue.length) {
                setCurrentRetryIndex(0);
              }
            }, 600);
          }
          return newQueue;
        });
      }

      // Play chime and advance after a delay (but not in retry phase where we handle advancement in setFailedQueue)
      playChime().then(() => {
        if (!isRetryPhase) {
          setTimeout(() => {
            advanceToNext();
          }, 500);
        }
      });
    } else {
      // Incorrect answer

      // Update fact score: -1 for incorrect
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore - 1;
        newScores.set(currentFact.factId, newScore);

        // If score goes below 1, decrement counter (was previously counted but now incorrect)
        if (currentScore >= 1 && newScore < 1) {
          setCompletedCount(prevCount => prevCount - 1);
        }

        return newScores;
      });

      if (!correctAnswerGiven) {
        // First attempt was wrong - add to failed queue (only if not already there)
        if (!isRetryPhase) {
          setFailedQueue(prev => {
            const alreadyExists = prev.some(f => f.factId === currentFact.factId);
            if (!alreadyExists) {
              return [...prev, currentFact];
            }
            return prev;
          });
        } else {
          // In retry phase and still wrong - add back to end of failed queue
          const failedFact = failedQueue[currentRetryIndex];
          setFailedQueue(prev => [...prev.filter((_, index) => index !== currentRetryIndex), failedFact]);
        }
      }

      // Reset to allow another attempt after feedback
      setTimeout(() => {
        setHasAnswered(false);
      }, PRACTICE_TIMER_CONSTANTS.WRONG_ANSWER_RETRY_MS);
    }
  }, [currentTarget, hasAnswered, isTTSPlaying, playChime, correctAnswerGiven, isRetryPhase, currentFact, failedQueue, currentRetryIndex, advanceToNext]);

  if (!currentSequence) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.WE_DO}
        onBack={onBack}
        progress={completedCount / factsToPractice.length}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        {/* Side-by-side layout container */}
        <div className="w-full max-w-6xl flex items-center justify-center gap-24">

          {/* Ready Phase: Waiting */}
          {!showSequence && (
            <div className="text-center">
              <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">Get ready...</div>
              <div className="text-lg text-gray-500">What comes next?</div>
            </div>
          )}

          {/* Sequence Display Phase */}
          {showSequence && (
            <>
              {/* Left Side: Number Sequence Display */}
              <div className="flex-shrink-0" style={{ width: '300px', height: '400px' }}>
                <div className="relative w-full h-full flex flex-col items-center justify-center">
                  {/* Number sequence display */}
                  <div className="flex items-center gap-6 mb-8">
                    {currentSequence.sequence.map((number, index) => (
                      <motion.div
                        key={`seq-${index}`}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, delay: index * 0.2 }}
                        className="w-20 h-20 lg:w-24 lg:h-24 bg-blue-600 text-white rounded-3xl flex items-center justify-center text-4xl lg:text-5xl font-bold shadow-lg"
                        style={NUMERAL_FONT_STYLE}
                      >
                        {number}
                      </motion.div>
                    ))}

                    {/* Question mark for the missing number */}
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{
                        scale: 1,
                        opacity: 1,
                        boxShadow: highlightCorrect ?
                          ['0 0 0 0 rgba(34, 197, 94, 0.7)', '0 0 0 20px rgba(34, 197, 94, 0)', '0 0 0 0 rgba(34, 197, 94, 0)'] :
                          undefined
                      }}
                      transition={{
                        duration: 0.4,
                        delay: currentSequence.sequence.length * 0.2,
                        boxShadow: { duration: 2, repeat: highlightCorrect ? 2 : 0 }
                      }}
                      className={`w-20 h-20 lg:w-24 lg:h-24 ${highlightCorrect ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'} rounded-3xl flex items-center justify-center text-4xl lg:text-5xl font-bold shadow-lg border-4 ${highlightCorrect ? 'border-green-700' : 'border-dashed border-gray-500'}`}
                      style={NUMERAL_FONT_STYLE}
                    >
                      {highlightCorrect ? currentSequence.targetNumber : '?'}
                    </motion.div>
                  </div>

                  {/* Instruction text */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                    className="text-xl lg:text-2xl font-semibold text-gray-700 text-center"
                  >
                    What comes next?
                  </motion.div>
                </div>
              </div>

              {/* Right Side: Answer Choices - 2x2 grid */}
              <div className="w-full max-w-md lg:max-w-lg">
                <div className="grid grid-cols-2 gap-8 lg:gap-12">
                  {answerChoices.map((choice, index) => (
                    <motion.button
                      key={choice}
                      initial={{ opacity: 0, y: 30, rotate: -5 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        rotate: 0,
                        scale: highlightCorrect && choice === currentTarget ? [1, 1.1, 1] : 1,
                        boxShadow: highlightCorrect && choice === currentTarget ?
                          ['0 0 0 0 rgba(34, 197, 94, 0.7)', '0 0 0 20px rgba(34, 197, 94, 0)', '0 0 0 0 rgba(34, 197, 94, 0)'] :
                          undefined
                      }}
                      transition={{
                        duration: 0.4,
                        delay: index * 0.1,
                        scale: { duration: highlightCorrect && choice === currentTarget ? 2 : 0.4, repeat: highlightCorrect && choice === currentTarget ? 1 : 0 }
                      }}
                      whileHover={!hasAnswered && !isTTSPlaying ? { scale: 1.08, rotate: 1 } : {}}
                      whileTap={!hasAnswered && !isTTSPlaying ? { scale: 0.92 } : {}}
                      onClick={() => handleAnswerClick(choice)}
                      disabled={hasAnswered || isTTSPlaying}
                      className={`
                        h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                        ${highlightCorrect && choice === currentTarget
                          ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300 animate-pulse'
                          : hasAnswered && choice === currentTarget
                          ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                          : hasAnswered || isTTSPlaying
                          ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer hover:shadow-2xl transform-gpu'
                        }
                        ${hasAnswered || isTTSPlaying ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
                      `}
                      style={NUMERAL_FONT_STYLE}
                    >
                      {choice}
                    </motion.button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatComesNextWeDoScreen;